// Quelle: /opt/lilasprojekte/frontend/src/store/useAuthStore.ts (Stand 2026-09-29)
// Geändert: Importe aus ../lib/fetchJson (setzeAbmeldeHandler) und ../api/client (abmelden);
// abgemeldet()/abmelden() leeren zusätzlich den Query-Zwischenspeicher und die Chat-Sitzungen
// (SPEC 03 §3.1, §9.14.2). Der Chat wird DYNAMISCH geladen: chatStore abonniert seinerseits
// diesen Speicher beim Laden — ein statischer Import in beide Richtungen wäre ein Kreis, der je
// nach Ladereihenfolge an einer noch nicht belegten Konstante scheitert.
// ============================================================
// Anmeldestatus — der kleine Speicher, an dem der Wächter in App.tsx hängt.
//
// Drei Zustände, und der Startwert ist WICHTIG:
//   "unbekannt"  GET /me läuft noch → leerer Rahmen, KEINE Umleitung
//   "anonym"     abgemeldet → Anmeldung, kein Toast
//   "angemeldet" alles offen
// Mit "anonym" als Startwert sähe ein angemeldeter Mensch bei jedem Neuladen
// für den Bruchteil einer Sekunde die Anmeldung.
//
// Die PERSON selbst (`Ich`) liegt NICHT hier, sondern in TanStack Query unter
// `['ich']`: sie ist eine Serverantwort und wird von `PATCH /me` fortgeschrieben.
// Hier steht nur, OB jemand angemeldet ist — zwei Wahrheiten über dieselbe
// Person wären eine zu viel.
//
// Die 401-Registrierung steht am Fuß der Datei und läuft beim LADEN des
// Moduls: `lib/fetchJson.ts` darf diesen Speicher nicht importieren (Kreis), also
// meldet der Speicher sich dort an. Damit setzt jeder 401 aus irgendeinem
// Aufruf den Status auf "anonym", und der Wächter führt zur Anmeldung.
// ============================================================
import { create } from "zustand";
import { setzeAbmeldeHandler } from "../lib/fetchJson";
import { abmelden as apiAbmelden } from "../api/client";
import { queryClient } from "../lib/abfrage";

export type AuthStatus = "unbekannt" | "anonym" | "angemeldet";

interface AuthZustand {
  status: AuthStatus;
  /** GET /me war erfolgreich — oder POST /auth/verify. */
  angemeldet(): void;
  /** GET /me hat 401 geantwortet, oder irgendein anderer Aufruf. */
  abgemeldet(): void;
  /** POST /auth/logout und Status zurück auf „anonym". */
  abmelden(): Promise<void>;
}

/** Alles, was nach dem Abmelden nicht im Browser bleiben darf (E13: kein Wortlaut im Speicher). */
function aufraeumen(): void {
  queryClient.clear();
  void import("../screens/ki/chatStore")
    .then((m) => m.chatAbmelden())
    .catch(() => {
      /* Chat nie geladen (kein Recht „assistent“) — nichts abzubrechen */
    });
}

export const useAuthStore = create<AuthZustand>((set, get) => ({
  status: "unbekannt",

  angemeldet() {
    set({ status: "angemeldet" });
  },

  abgemeldet() {
    const vorher = get().status;
    set({ status: "anonym" });
    // Nur beim Übergang aus „angemeldet“: der Startfall („unbekannt“ → 401) hat nichts zu leeren,
    // und ein zweiter 401 aus einer parallelen Abfrage soll nicht noch einmal räumen.
    if (vorher === "angemeldet") aufraeumen();
  },

  async abmelden() {
    try {
      await apiAbmelden();
    } catch {
      // Auch ein fehlgeschlagener Abmeldeversuch beendet die Sitzung im
      // Browser — alles andere ließe jemanden vor einem Bildschirm sitzen,
      // der so tut, als wäre er noch angemeldet.
    }
    set({ status: "anonym" });
    aufraeumen();
  },
}));

// EINMAL beim Laden des Moduls. main.tsx importiert App.tsx, App.tsx diesen
// Speicher — die Zeile läuft also, bevor die erste Anfrage hinausgeht.
setzeAbmeldeHandler(() => {
  useAuthStore.getState().abgemeldet();
});
